import React, { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Plus,
  Search,
  SlidersHorizontal,
  ChevronDown,
  MoreHorizontal,
  Tag,
  Layers,
  CheckCircle2,
  XCircle,
  Edit2,
  Trash2,
  PenTool,
  Code2,
  Gavel,
  Briefcase,
  User,
  Phone,
  MessageSquare,
  Globe2,
  Database,
  ShieldCheck,
  Server,
  Cpu,
  Monitor,
  Zap,
  Settings,
  FileText,
  FileSignature,
  BookOpenCheck,
  Scale,
  Building2,
  Headphones,
  CalendarClock,
  ClipboardList,
  Inbox,
  GlobeLock,
} from "lucide-react";
import BackModule from "./back.tsx";

// ------------------------------------
// Types
// ------------------------------------

type IconCategoryKey = "design" | "dev" | "legal" | "business" | "comms" | "misc";

interface IconDef {
  key: string; // e.g. "design.ui", "legal.contracts"
  label: string;
  category: IconCategoryKey;
  Icon: LucideIcon;
}

interface Subskill {
  id: number;
  name: string;
  description: string;
  status: "active" | "inactive";
}

interface Skill {
  id: number;
  name: string;
  description: string;
  categoryId: number;
  status: "active" | "inactive";
  agents: number;
  subskills: Subskill[];
  iconKey?: string; // maps to IconDef.key
}

interface Category {
  id: number;
  name: string;
  color: string; // Tailwind class combo
  sortOrder: number;
}

// ------------------------------------
// Icon catalog (reusable across Chamcore)
// ------------------------------------

const ICON_DEFS: IconDef[] = [
  // Design
  { key: "design.ui", label: "UI / UX", category: "design", Icon: PenTool },
  { key: "design.wireframe", label: "Wireframing", category: "design", Icon: LayoutIcon },
  { key: "design.prototype", label: "Prototyping", category: "design", Icon: Monitor },
  { key: "design.brand", label: "Brand / Visual", category: "design", Icon: PaletteIcon },

  // Development
  { key: "dev.fullstack", label: "Fullstack", category: "dev", Icon: Code2 },
  { key: "dev.backend", label: "Backend", category: "dev", Icon: Server },
  { key: "dev.frontend", label: "Frontend", category: "dev", Icon: Monitor },
  { key: "dev.api", label: "API / Integrasjoner", category: "dev", Icon: Database },
  { key: "dev.architecture", label: "Arkitektur", category: "dev", Icon: Cpu },

  // Legal
  { key: "legal.contracts", label: "Kontrakter", category: "legal", Icon: FileSignature },
  { key: "legal.compliance", label: "Compliance", category: "legal", Icon: ShieldCheck },
  { key: "legal.gdpr", label: "GDPR", category: "legal", Icon: GlobeLock },
  { key: "legal.disputes", label: "Tvister", category: "legal", Icon: Gavel },
  { key: "legal.corporate", label: "Selskapsrett", category: "legal", Icon: Building2 },

  // Business / Advisory
  { key: "business.strategy", label: "Strategi", category: "business", Icon: Briefcase },
  { key: "business.finance", label: "Finans", category: "business", Icon: FileText },
  { key: "business.operations", label: "Drift", category: "business", Icon: ClipboardList },
  { key: "business.training", label: "Opplæring", category: "business", Icon: BookOpenCheck },
  { key: "business.management", label: "Ledelse", category: "business", Icon: Scale },

  // Communication / Customer
  { key: "comms.phone", label: "Telefoni", category: "comms", Icon: Phone },
  { key: "comms.chat", label: "Chat", category: "comms", Icon: MessageSquare },
  { key: "comms.email", label: "E-post", category: "comms", Icon: Inbox },
  { key: "comms.support", label: "Support", category: "comms", Icon: Headphones },
  { key: "comms.meetings", label: "Møter", category: "comms", Icon: CalendarClock },

  // Misc / System
  { key: "misc.user", label: "Person", category: "misc", Icon: User },
  { key: "misc.settings", label: "Settings", category: "misc", Icon: Settings },
  { key: "misc.global", label: "Global", category: "misc", Icon: Globe2 },
  { key: "misc.performance", label: "Ytelse", category: "misc", Icon: Zap },
  { key: "misc.layered", label: "Lag / Struktur", category: "misc", Icon: Layers },
];

// Fallback icons for the few we referenced but are not default lucide exports
// We alias to existing ones to keep lucide import minimal and working.

function LayoutIcon(props: React.SVGProps<SVGSVGElement>) {
  return <Monitor {...props} />;
}

function PaletteIcon(props: React.SVGProps<SVGSVGElement>) {
  return <Settings {...props} />;
}

const ICON_MAP: Record<string, IconDef> = ICON_DEFS.reduce((acc, def) => {
  acc[def.key] = def;
  return acc;
}, {} as Record<string, IconDef>);

const ICON_CATEGORY_LABELS: Record<IconCategoryKey, string> = {
  design: "Design",
  dev: "Utvikling",
  legal: "Juridisk",
  business: "Business",
  comms: "Kommunikasjon",
  misc: "Annet",
};

// ------------------------------------
// Helpers & small components
// ------------------------------------

function classNames(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <span
    className={classNames(
      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border border-slate-200 bg-slate-50 text-slate-700",
      className,
    )}
  >
    {children}
  </span>
);

const TogglePill: React.FC<{
  checked: boolean;
  onChange: () => void;
  labelOn?: string;
  labelOff?: string;
}> = ({ checked, onChange, labelOn = "Aktiv", labelOff = "Inaktiv" }) => (
  <button
    type="button"
    onClick={onChange}
    className={classNames(
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium transition-colors border",
      checked
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-slate-50 text-slate-500 border-slate-200",
    )}
  >
    {checked ? (
      <>
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {labelOn}
      </>
    ) : (
      <>
        <XCircle className="h-3 w-3 mr-1" />
        {labelOff}
      </>
    )}
  </button>
);

const PillButton: React.FC<{
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  icon: Icon,
  children,
  className = "",
  ...props
}) => (
    <button
      type={props.type || "button"}
      className={classNames(
        "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition",
        className,
      )}
      {...props}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </button>
  );

// ------------------------------------
// Reusable IconPicker component
// ------------------------------------

interface IconPickerProps {
  value?: string;
  onChange: (key: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange }) => {
  const [activeFilter, setActiveFilter] = useState<IconCategoryKey | "all">("all");

  const filteredIcons = ICON_DEFS.filter((icon) =>
    activeFilter === "all" ? true : icon.category === activeFilter,
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => setActiveFilter("all")}
          className={classNames(
            "rounded-full px-3 py-1 text-[11px] font-medium border",
            activeFilter === "all"
              ? "border-sky-400 bg-sky-50 text-sky-700"
              : "border-slate-200 bg-slate-50 text-slate-600",
          )}
        >
          Alle
        </button>
        {(Object.keys(ICON_CATEGORY_LABELS) as IconCategoryKey[]).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveFilter(cat)}
            className={classNames(
              "rounded-full px-3 py-1 text-[11px] font-medium border",
              activeFilter === cat
                ? "border-sky-400 bg-sky-50 text-sky-700"
                : "border-slate-200 bg-slate-50 text-slate-600",
            )}
          >
            {ICON_CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-6 gap-2 p-2 rounded-xl border border-slate-200 bg-slate-50 max-h-56 overflow-y-auto">
        {filteredIcons.map((def) => {
          const isActive = def.key === value;
          const IconComp = def.Icon;
          return (
            <button
              key={def.key}
              type="button"
              onClick={() => onChange(def.key)}
              className={classNames(
                "flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 hover:bg-white transition text-[10px]",
                isActive ? "border-sky-400 bg-white text-sky-700" : "border-slate-200 text-slate-600",
              )}
            >
              <IconComp className="h-5 w-5" />
              <span className="truncate w-full text-[9px]">{def.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ------------------------------------
// Seed data
// ------------------------------------

const initialCategories: Category[] = [
  { id: 1, name: "Design", color: "bg-sky-100 text-sky-700", sortOrder: 1 },
  { id: 2, name: "Utvikling", color: "bg-emerald-100 text-emerald-700", sortOrder: 2 },
  { id: 3, name: "Rådgivning", color: "bg-amber-100 text-amber-700", sortOrder: 3 },
];

const initialSkills: Skill[] = [
  {
    id: 1,
    name: "UI/UX Design",
    description: "Brukeropplevelse, wireframes og prototyping.",
    categoryId: 1,
    status: "active",
    agents: 12,
    iconKey: "design.ui",
    subskills: [
      { id: 11, name: "Wireframing", description: "Low-fi skisser", status: "active" },
      { id: 12, name: "Prototyping", description: "Interaktive prototyper", status: "active" },
      { id: 13, name: "Brukertesting", description: "Test med faktiske brukere", status: "inactive" },
    ],
  },
  {
    id: 2,
    name: "Fullstack Development",
    description: "Frontend, backend og integrasjoner.",
    categoryId: 2,
    status: "active",
    agents: 8,
    iconKey: "dev.fullstack",
    subskills: [
      { id: 21, name: "React", description: "SPA og komponenter", status: "active" },
      { id: 22, name: "PHP", description: "API og backend", status: "active" },
    ],
  },
  {
    id: 3,
    name: "Juridisk rådgivning",
    description: "Kontrakter, avtaler og compliance.",
    categoryId: 3,
    status: "inactive",
    agents: 5,
    iconKey: "legal.contracts",
    subskills: [
      { id: 31, name: "Kontraktsrett", description: "", status: "active" },
      { id: 32, name: "Personvern (GDPR)", description: "", status: "active" },
    ],
  },
];

// ------------------------------------
// Skill card
// ------------------------------------

const SkillCard: React.FC<{
  skill: Skill;
  category?: Category;
  onEdit: () => void;
  onManageSubskills: () => void;
  onDelete: () => void;
  onToggle: () => void;
}> = ({ skill, category, onEdit, onManageSubskills, onDelete, onToggle }) => {
  const active = skill.status === "active";
  const iconDef = skill.iconKey ? ICON_MAP[skill.iconKey] : undefined;
  const HeaderIcon = iconDef?.Icon || Layers;

  return (
    <div className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition cursor-pointer">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <HeaderIcon className="h-4 w-4 text-sky-600" />
              <h3 className="text-sm font-semibold text-slate-900">{skill.name}</h3>
            </div>
            {category && (
              <Badge className={classNames("border-0", category.color)}>
                <Tag className="h-3 w-3 mr-1" />
                {category.name}
              </Badge>
            )}
          </div>
          {skill.description && (
            <p className="text-xs text-slate-500 max-w-sm">{skill.description}</p>
          )}
        </div>
        <button
          type="button"
          className="rounded-full p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 border border-slate-100">
          <Layers className="h-3 w-3" />
          {skill.subskills?.length || 0} subskills
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 border border-slate-100">
          <CheckCircle2 className="h-3 w-3" />
          {skill.agents} agenter
        </span>
        <TogglePill
          checked={active}
          onChange={onToggle}
          labelOn="Aktiv"
          labelOff="Inaktiv"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <PillButton icon={Edit2} onClick={onEdit}>
          Rediger skill
        </PillButton>
        <PillButton icon={Layers} onClick={onManageSubskills}>
          Administrer subskills
        </PillButton>
        <PillButton
          icon={Trash2}
          className="ml-auto border-rose-200 text-rose-600 hover:bg-rose-50"
          onClick={onDelete}
        >
          Slett
        </PillButton>
      </div>
    </div>
  );
};

// ------------------------------------
// Modal + form fields
// ------------------------------------

const Modal: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
}> = ({ open, title, onClose, children, width = "max-w-lg" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
      <div className={classNames("w-full rounded-2xl bg-white shadow-xl", width)}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

const InputField: React.FC<{
  label: string;
  hint?: string;
} & React.InputHTMLAttributes<HTMLInputElement>> = ({ label, hint, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-slate-700">{label}</label>
    <input
      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
      {...props}
    />
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const TextAreaField: React.FC<{
  label: string;
  hint?: string;
  rows?: number;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  label,
  hint,
  rows = 3,
  ...props
}) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <textarea
        rows={rows}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
        {...props}
      />
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );

const SelectField: React.FC<{
  label: string;
  options: Category[];
  value: number | "";
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  hint?: string;
}> = ({ label, options, value, onChange, hint }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-slate-700">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
      >
        <option value="">Velg kategori</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

// ------------------------------------
// Main page component
// ------------------------------------

export default function SkillsModule({ setTab }: { setTab: any }) {
  const [categories] = useState<Category[]>(initialCategories);
  const [skills, setSkills] = useState<Skill[]>(initialSkills);
  const [search, setSearch] = useState<string>("");
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>("all");

  const [skillModalOpen, setSkillModalOpen] = useState<boolean>(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);

  const [subskillModalOpen, setSubskillModalOpen] = useState<boolean>(false);
  const [activeSkillForSubskills, setActiveSkillForSubskills] = useState<Skill | null>(null);

  const filteredSkills = skills.filter((skill) => {
    const matchesSearch =
      !search ||
      skill.name.toLowerCase().includes(search.toLowerCase()) ||
      skill.description.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      activeCategoryFilter === "all" || skill.categoryId === Number(activeCategoryFilter);

    return matchesSearch && matchesCategory;
  });

  const openCreateSkill = () => {
    const nextId = skills.length ? Math.max(...skills.map((s) => s.id)) + 1 : 1;
    setEditingSkill({
      id: nextId,
      name: "",
      description: "",
      categoryId: categories[0]?.id || 1,
      status: "active",
      agents: 0,
      subskills: [],
      iconKey: undefined,
    });
    setSkillModalOpen(true);
  };

  const openEditSkill = (skill: Skill) => {
    setEditingSkill({ ...skill });
    setSkillModalOpen(true);
  };

  const handleSaveSkill = () => {
    if (!editingSkill) return;

    const exists = skills.some((s) => s.id === editingSkill.id);
    if (exists) {
      setSkills(skills.map((s) => (s.id === editingSkill.id ? editingSkill : s)));
    } else {
      setSkills([...skills, editingSkill]);
    }

    setSkillModalOpen(false);
    setEditingSkill(null);
  };

  const handleDeleteSkill = (skillId: number) => {
    setSkills(skills.filter((s) => s.id !== skillId));
  };

  const handleToggleSkill = (skillId: number) => {
    setSkills(
      skills.map((s) =>
        s.id === skillId ? { ...s, status: s.status === "active" ? "inactive" : "active" } : s,
      ),
    );
  };

  const openSubskillsModal = (skill: Skill) => {
    setActiveSkillForSubskills({ ...skill });
    setSubskillModalOpen(true);
  };

  const handleSaveSubskills = () => {
    if (!activeSkillForSubskills) return;
    setSkills(skills.map((s) => (s.id === activeSkillForSubskills.id ? activeSkillForSubskills : s)));
    setSubskillModalOpen(false);
    setActiveSkillForSubskills(null);
  };

  const addSubskillRow = () => {
    setActiveSkillForSubskills((prev) => {
      if (!prev) return prev;
      const nextId = prev.subskills.length
        ? Math.max(...prev.subskills.map((ss) => ss.id)) + 1
        : 1;
      return {
        ...prev,
        subskills: [
          ...prev.subskills,
          { id: nextId, name: "Ny subskill", description: "", status: "active" },
        ],
      };
    });
  };

  const updateSubskillField = (id: number, field: keyof Subskill, value: string) => {
    setActiveSkillForSubskills((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subskills: prev.subskills.map((ss) => (ss.id === id ? { ...ss, [field]: value } : ss)),
      };
    });
  };

  const toggleSubskillStatus = (id: number) => {
    setActiveSkillForSubskills((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subskills: prev.subskills.map((ss) =>
          ss.id === id
            ? { ...ss, status: ss.status === "active" ? "inactive" : "active" }
            : ss,
        ),
      };
    });
  };

  const deleteSubskill = (id: number) => {
    setActiveSkillForSubskills((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subskills: prev.subskills.filter((ss) => ss.id !== id),
      };
    });
  };

  const showBackBtn = localStorage.getItem("previousPage") ? true : false;

  const handleNavigation = (to: string) => {
    localStorage.setItem("previousPage", "Agent detail");
    setTab(to);
  }

  return (
    <>
      {showBackBtn && (
        <BackModule setTab={setTab} label={localStorage.getItem("previousPage") || ""} />
      )}
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Top bar */}
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
          <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-[11px] uppercase tracking-wide text-sky-500 font-semibold">
                Teletorget · Administrasjon
              </span>
              <h1 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                Skills Maintenance
                <Badge className="bg-sky-50 text-sky-700 border-sky-100">
                  Hovedskills & underkategorier
                </Badge>
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <PillButton icon={SlidersHorizontal}>Kategoriinnstillinger</PillButton>
              <button
                type="button"
                onClick={openCreateSkill}
                className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 transition"
              >
                <Plus className="h-4 w-4" />
                Ny hovedskill
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1">
          <div className="mx-auto max-w-6xl px-4 py-4 flex flex-col gap-4">
            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Søk på skill, beskrivelse eller subskill..."
                  className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <PillButton
                  icon={Layers}
                  className={classNames(
                    activeCategoryFilter === "all"
                      ? "bg-sky-50 border-sky-200 text-sky-700"
                      : "",
                  )}
                  onClick={() => setActiveCategoryFilter("all")}
                >
                  Alle kategorier
                </PillButton>
                {categories.map((cat) => (
                  <PillButton
                    key={cat.id}
                    onClick={() => setActiveCategoryFilter(String(cat.id))}
                    className={classNames(
                      activeCategoryFilter === String(cat.id)
                        ? "bg-sky-50 border-sky-200 text-sky-700"
                        : "",
                    )}
                  >
                    <SlidersHorizontal className="h-3 w-3 text-sky-600" />
                    {cat.name}
                  </PillButton>
                ))}
              </div>
            </div>

            {/* Skills grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredSkills.map((skill) => {
                const category = categories.find((c) => c.id === skill.categoryId);
                return (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    category={category}
                    onEdit={() => openEditSkill(skill)}
                    onManageSubskills={() => openSubskillsModal(skill)}
                    onDelete={() => handleDeleteSkill(skill.id)}
                    onToggle={() => handleToggleSkill(skill.id)}
                  />
                );
              })}

              {filteredSkills.length === 0 && (
                <div className="col-span-full">
                  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-10 text-center">
                    <Layers className="h-8 w-8 text-slate-300 mb-2" />
                    <p className="text-sm font-medium text-slate-700">
                      Ingen skills matcher filteret.
                    </p>
                    <p className="text-xs text-slate-500 max-w-sm">
                      Juster søk eller kategori, eller opprett en ny hovedskill for å komme i gang.
                    </p>
                    <button
                      type="button"
                      onClick={openCreateSkill}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 transition"
                    >
                      <Plus className="h-4 w-4" />
                      Opprett første skill
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Skill modal */}
        <Modal
          open={skillModalOpen}
          title={editingSkill ? "Rediger hovedskill" : "Ny hovedskill"}
          onClose={() => {
            setSkillModalOpen(false);
            setEditingSkill(null);
          }}
        >
          {editingSkill && (
            <div className="flex flex-col gap-4">
              <InputField
                label="Navn på hovedskill"
                placeholder="F.eks. UI/UX Design"
                value={editingSkill.name}
                onChange={(e) =>
                  setEditingSkill({ ...editingSkill, name: e.target.value })
                }
              />
              <TextAreaField
                label="Beskrivelse"
                placeholder="Kort forklaring på hva denne skillen dekker."
                value={editingSkill.description}
                onChange={(e) =>
                  setEditingSkill({ ...editingSkill, description: e.target.value })
                }
              />
              <SelectField
                label="Kategori"
                options={categories}
                value={editingSkill.categoryId}
                onChange={(e) =>
                  setEditingSkill({
                    ...editingSkill,
                    categoryId: Number(e.target.value),
                  })
                }
                hint="Bruk kategorier for å samle relaterte skills."
              />

              {/* Icon picker */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">Ikon</label>
                <IconPicker
                  value={editingSkill.iconKey}
                  onChange={(key) =>
                    setEditingSkill({
                      ...editingSkill,
                      iconKey: key,
                    })
                  }
                />
                <p className="text-[11px] text-slate-400">
                  Velg et ikon for skillen. Nøkkelen lagres som f.eks. "design.ui" og kan brukes på tvers
                  av Chamcore.
                </p>
              </div>

              <div className="flex items-center justify-between mt-2">
                <TogglePill
                  checked={editingSkill.status === "active"}
                  onChange={() =>
                    setEditingSkill({
                      ...editingSkill,
                      status:
                        editingSkill.status === "active" ? "inactive" : "active",
                    })
                  }
                />
                <p className="text-[11px] text-slate-400">
                  Aktive skills blir synlige i agent-profiler og matching.
                </p>
              </div>

              <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setSkillModalOpen(false);
                    setEditingSkill(null);
                  }}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleSaveSkill}
                  className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700"
                >
                  Lagre skill
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Subskills modal */}
        <Modal
          open={subskillModalOpen}
          title={
            activeSkillForSubskills
              ? `Subskills for ${activeSkillForSubskills.name}`
              : "Subskills"
          }
          onClose={() => {
            setSubskillModalOpen(false);
            setActiveSkillForSubskills(null);
          }}
          width="max-w-3xl"
        >
          {activeSkillForSubskills && (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-slate-500">
                Definer underkategorier (subskills) som gjør det enklere å matche riktige agenter og
                synliggjøre kompetanse. Disse vil vises som tags i agentprofilen.
              </p>

              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-100 text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">Navn</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500">
                        Beskrivelse
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-500 w-28">
                        Status
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-slate-500 w-12" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {activeSkillForSubskills.subskills.map((ss) => (
                      <tr key={ss.id}>
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-start gap-1">
                            <Layers className="h-3 w-3 text-sky-500 mt-1" />
                            <input
                              value={ss.name}
                              onChange={(e) =>
                                updateSubskillField(ss.id, "name", e.target.value)
                              }
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <textarea
                            rows={2}
                            value={ss.description}
                            onChange={(e) =>
                              updateSubskillField(ss.id, "description", e.target.value)
                            }
                            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <button
                            type="button"
                            onClick={() => toggleSubskillStatus(ss.id)}
                            className={classNames(
                              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                              ss.status === "active"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-slate-50 text-slate-500",
                            )}
                          >
                            {ss.status === "active" ? (
                              <>
                                <CheckCircle2 className="h-3 w-3" /> Aktiv
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3 w-3" /> Inaktiv
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <button
                            type="button"
                            onClick={() => deleteSubskill(ss.id)}
                            className="rounded-full p-1 text-rose-500 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {activeSkillForSubskills.subskills.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-6 text-center text-xs text-slate-400"
                        >
                          Ingen subskills definert ennå. Legg til den første for å komme i gang.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={addSubskillRow}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Legg til subskill
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSubskillModalOpen(false);
                      setActiveSkillForSubskills(null);
                    }}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveSubskills}
                    className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700"
                  >
                    Lagre subskills
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

